import { useState, useEffect } from "react";
import { Space, Button, Row, Col, Input, Form, notification, Typography, Select } from "antd";
import { LeftCircleOutlined } from "@ant-design/icons";
import { useGetLocationsQuery } from "../../../redux/services/locationsApi";
import { useGetUsersQuery } from "../../../redux/services/userApi";
import { useGetPartnersQuery } from "../../../redux/services/partnerApi";
import { useNavigate, useParams } from "react-router-dom";
import {
  useCreateVisitMutation,
  useLazyGetVisitByIdQuery,
  useUpdateVisitMutation
} from "../../../redux/services/visitApi";
import { useLazyGetBrandModelsQuery } from "../../../redux/services/brandModelApi";
import ErrorDetails from "../../../components/ErrorDetails";
import { useLazyGetComplaintByIdQuery } from "../../../redux/services/complaintApi";
import {
  AltContactNumberInputElement,
  BrandModelSelect,
  BrandSelect,
  CheckBoxInput,
  CityManagerSelect,
  CitySelect,
  ContactNumberInputElement,
  DropDownSelectProps,
  InputElement,
  PincodeInputElement,
  StateSelect,
  ZoneSelect
} from "../common/FormsElements";
import { localizeDateTime } from "../../../utils";
import { useAppSelector } from "../../../redux/hooks";
import { broadVisitFinding1Options, yesNoOptions } from "../../../constants/helperConstants";

const VisitForm = (props: any) => {
  const navigate = useNavigate();
  const params = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [showOtherReasonField, setShowOtherReasonField] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const onGoBack = () => {
    navigate(-1);
  };

  const complaintId = params?.complaintId;
  const visitId = params?.visitId;

  const isEditMode = visitId != null;

  const [getComplaintById, { data: complaintData }] = useLazyGetComplaintByIdQuery();
  const [getVisitById, { data: visitData }] = useLazyGetVisitByIdQuery();

  const selectedComplaintData = complaintData?.complaint;
  const selectedVisitData = visitData?.visit;

  const [triggerGetBrandModels, { data: brandModelsData }] = useLazyGetBrandModelsQuery();
  const brandModels = brandModelsData?.brand_models || [];

  const partnerIdFromVisit = selectedVisitData?.visit_partner?.id;
  const [partnerId, setPartnerId] = useState<string>();

  const [locationId, setLocationId] = useState(null);

  const { data: partnerData, refetch: refetchPartners } = useGetPartnersQuery({
    all_pages: true,
    location_id: locationId
  });

  useEffect(() => {
    if (locationId) {
      refetchPartners();
    }
  }, [locationId]);

  useEffect(() => {
    setLocationId(isEditMode ? selectedVisitData?.city?.id : selectedComplaintData?.city?.id);
  }, [selectedVisitData, selectedComplaintData]);

  useEffect(() => {
    if (visitId) {
      getVisitById(visitId);
    }
  }, [visitId]);

  useEffect(() => {
    const brandId = visitData?.visit?.brand?.id;
    if (brandId) {
      triggerGetBrandModels({ all_pages: true, brand_id: brandId });
    }
  }, [selectedComplaintData, selectedVisitData]);

  const onCityChange = (cityLocationId: any) => {
    setLocationId(cityLocationId);
  };

  const { data: fieldAgentData } = useGetUsersQuery({
    role_type: "field_agent",
    partner_id: partnerId || partnerIdFromVisit,
    all_pages: true
  });
  const fieldAgents = partnerId || partnerIdFromVisit ? fieldAgentData?.users || [] : [];

  const { data: partnerManagerData } = useGetUsersQuery({
    role_type: "partner_manager",
    partner_id: partnerId || partnerIdFromVisit
  });
  const partnerManager = partnerId || partnerIdFromVisit ? partnerManagerData?.users || [] : [];

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  const brandId = selectedVisitData?.brand?.id || selectedComplaintData?.brand?.id;

  const auth = useAppSelector<any>((store) => store.auth);
  const user = auth.user;
  const isAdminUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user", "management"].includes(role.role_type)
  );

  const isSuperUser = user?.roles?.some((role: { role_type: string }) =>
    ["super_user"].includes(role.role_type)
  );

  const isEditUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis", "super_user"].includes(role.role_type)
  );

  const isMisUser = user?.roles?.some((role: { role_type: string }) =>
    ["mis"].includes(role.role_type)
  );


  const isCityManager = user?.roles?.some((role: { role_type: string }) =>
    ["city_manager"].includes(role.role_type)
  );

  const handlePartnerChange = (value: string) => {
    form.setFieldsValue({
      visit_partner_id: value,
      assigned_field_agent1_id: null,
      assigned_field_agent2_id: null,
      assigned_partner_user_id: null
    });
    setPartnerId(value);
  };

  const handleSummaryStage1Change = (value: string) => {
    if (value === "Others") {
      form.setFieldsValue({
        summary_stage: value,
        summary_stage1: ""
      });
      setShowOtherReasonField(true);
    } else {
      form.setFieldsValue({
        summary_stage1: value,
        summary_stage: ""
      });
      setShowOtherReasonField(false);
    }
  };

  useEffect(() => {
    let fieldsValue: any;
    if (isEditMode === true) {
      let scheduled_visit_date_time = null;
      if (selectedVisitData?.scheduled_visit_date_time) {
        const localDateTime = localizeDateTime(selectedVisitData?.scheduled_visit_date_time);
        const [datePart, timePart] = localDateTime.split(", ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        scheduled_visit_date_time = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      fieldsValue = {
        ...selectedVisitData,
        complaint_id: selectedComplaintData?.id,
        visit_partner_id: selectedVisitData?.visit_partner?.id,
        assigned_partner_user_id: selectedVisitData?.assigned_partner_user?.id,
        assigned_field_agent1_id: selectedVisitData?.assigned_field_agent1?.id,
        assigned_field_agent2_id: selectedVisitData?.assigned_field_agent2?.id,
        scheduled_visit_date_time: scheduled_visit_date_time
      };
    } else {
      fieldsValue = {
        ...selectedComplaintData,
        complaint_id: selectedComplaintData?.id
      };
    }
    form.setFieldsValue({
      ...fieldsValue,
      brand_id: fieldsValue?.brand?.id,
      brand: fieldsValue?.brand?.name,
      complaint_id: fieldsValue?.id,
      city_id: fieldsValue?.city?.id,
      state_id: fieldsValue?.state?.id,
      assigned_city_manager_id: fieldsValue?.assigned_city_manager?.id,
      brand_model_id: fieldsValue?.brand_model?.id
    });

    const data = zones?.find((zone: any) => zone.id === fieldsValue?.zone?.id);
    if (data) {
      form.setFieldsValue({
        zone_id: fieldsValue?.zone?.id
      });
    }
  }, [selectedComplaintData, selectedVisitData]);

  useEffect(() => {
    if (complaintId) {
      getComplaintById(complaintId);
    }
  }, [complaintId]);

  const onFinish = (values: any) => {
    if (isEditMode !== true) {
      addVisit(values, setLoading, form);
    } else {
      onUpdateVisit(values, setLoading, form, visitId);
    }
  };

  const onFinishFailed = (errorInfo: any) => {
    api.info({
      message: "Operation failed",
      description: (
        <div>
          {errorInfo?.errorFields?.map((error: any) => (
            <p key={error?.name[0]}>
              {error?.name[0]} : {error?.errors?.join(",")}
            </p>
          ))}
        </div>
      ),
      placement: "top"
    });
    console.log("Failed:", errorInfo);
  };

  const [createVisit] = useCreateVisitMutation();

  const addVisit = async (values: any, setLoading: any, form: any) => {
    try {
      setLoading(true);
      const scheduled_visit_date_time = new Date(values?.scheduled_visit_date_time).toISOString();
      const data = {
        ...values,
        complaint_id: selectedComplaintData?.id,
        scheduled_visit_date_time
      };
      await createVisit({ visit: data }).unwrap();
      api.success({
        message: "Visit created successfully!",
        placement: "top"
      });
      form.resetFields();
      setLoading(false);
      navigate("/visits");
    } catch (error: any) {
      api.info({
        message: "Visit creation failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  const [updateVisit] = useUpdateVisitMutation();

  const onUpdateVisit = async (values: any, setLoading: any, form: any, id: any) => {
    try {
      setLoading(true);
      const scheduled_visit_date_time = new Date(values?.scheduled_visit_date_time).toISOString();
      const data = { ...values, scheduled_visit_date_time };
      await updateVisit({ id, visit: data }).unwrap();
      api.success({
        message: "Visit updated successfully!",
        placement: "top"
      });
      setLoading(false);
      form.resetFields();
      if (window.history.length > 1) {
        history.back();
      } else {
        navigate("/visits");
      }
    } catch (error: any) {
      api.info({
        message: "Update Visit failed",
        description: <ErrorDetails errors={error?.data || []} />,
        placement: "top"
      });
      setLoading(false);
    }
  };

  return (
    <>
      {contextHolder}
      <div style={{ paddingBottom: 50 }}>
        <Row style={{ marginBottom: 20 }}>
          <Col
            sm={24}
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            {isEditMode ? <h2>Update Visit</h2> : <h2>Create Visit</h2>}
            <Button onClick={onGoBack} size="middle" type="default" icon={<LeftCircleOutlined />}>
              Back
            </Button>
          </Col>
        </Row>

        <Form
          name="visit_form"
          form={form}
          initialValues={{ remember: true }}
          labelCol={{ span: 9 }}
          wrapperCol={{ span: 11 }}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
          labelAlign="right"
        >
          <Typography style={{ fontWeight: 500 }}>Customer Details</Typography>
          <Row gutter={16}>
            <Col span={12}>
              <InputElement
                label="Customer Name"
                name="customer_name"
                rules={[{ required: true, message: "Please Enter Customer Name" }]}
              />
              <InputElement label="Email" name="email" />
            </Col>
            <Col span={12}>
              <ContactNumberInputElement />
              <AltContactNumberInputElement />
            </Col>
          </Row>

          <Typography style={{ fontWeight: 500 }}>Vehicle Details</Typography>
          <Row gutter={16}>
            <Col span={12}>
              <InputElement label="Dealer" name="dealer_name" disabled />
              <InputElement label="Dealer Location" name="dealer_location" disabled />
              <BrandModelSelect brandModels={brandModels} />
              <InputElement label="Model Variant" name="model_variant" />
              <InputElement label="Model Colour" name="model_colour" />
            </Col>
            <Col span={12}>
              <InputElement label="eCB Booking Code" name="ecb_booking_code" disabled />
              <BrandSelect
                onSelect={(val: string) => {
                  console.log(val);
                }}
                disabled
              />
              <InputElement label="Model Description" name="model_description" />
              <InputElement label="Model Int Color" name="model_int_color" />
              <InputElement label="Model Ext Color" name="model_ext_color" />
            </Col>
          </Row>

          <Typography style={{ fontWeight: 500 }}>Address and Schedule</Typography>
          <Row gutter={16}>
            <Col span={12}>
              <InputElement label="Visit Address Line1" name="address_line1" />

              <Form.Item
                label="Scheduled Visit Date Time"
                name="scheduled_visit_date_time"
                rules={[{ required: true, message: "Please input Scheduled visit date Time" }]}
              >
                <Input type="datetime-local" />
              </Form.Item>
              <InputElement label="Visit Locality" name="locality" />
              <InputElement label="Customer City" name="customer_city" />
              <CitySelect
                onChange={(cityLocationId: any) => {
                  onCityChange(cityLocationId);
                }}
              />
              <StateSelect />
              <InputElement label="Site POC Name" name="site_poc_name" />
            </Col>
            <Col span={12}>
              <PincodeInputElement />
              <InputElement label="Visit Address Line2" name="address_line2" />
              <ZoneSelect disabled={!isEditUser && !isCityManager} required />
              <InputElement label="Latitude" name="latitude" />
              <InputElement label="Longitude" name="longitude" />
              <Form.Item
                label="Is Outstation ?"
                name="is_outstation"
                rules={[{ required: true, message: "Please select outstation" }]}
              >
                <Select
                  placeholder="Select Outstation"
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(inputValue, option: any) =>
                    option.label.toLowerCase().includes(inputValue.toLowerCase())
                  }
                  options={yesNoOptions}
                ></Select>
              </Form.Item>
              <InputElement label="Site POC Contact" name="site_poc_contact" />
            </Col>
          </Row>

          <Typography style={{ fontWeight: 500 }}>Assignment</Typography>
          <Row gutter={16}>
            <Col span={12}>
              <CityManagerSelect />
              <DropDownSelectProps
                label="Assigned Field Agent1"
                name="assigned_field_agent1_id"
                records={fieldAgents}
                placeHolder="Select field agent"
              />
              <DropDownSelectProps
                label="Assigned Field Agent2"
                name="assigned_field_agent2_id"
                records={fieldAgents}
                placeHolder="Select field agent"
              />
            </Col>
            <Col span={12}>
              <DropDownSelectProps
                label="Visit Partner"
                name="visit_partner_id"
                required
                records={partnerData?.partners}
                placeHolder="Select partner"
                onSelect={handlePartnerChange}
              />

              <DropDownSelectProps
                label="Assigned Partner User"
                name="assigned_partner_user_id"
                records={partnerManager}
                placeHolder="Select partner user"
              />
              <InputElement label="Note For Agent" name="note_for_agent" />
            </Col>
          </Row>

          <Typography style={{ fontWeight: 500 }}>Other</Typography>
          <Row gutter={16}>
            <Col span={12}>
              <CheckBoxInput label="Customer Visit Confirmed" name="customer_visit_confirmed" />
              <CheckBoxInput label="Customer Ok With Recording" name="customer_ok_with_recording" />
              <DropDownSelectProps
                label="Broad Visit Findings 1"
                name={showOtherReasonField ? "summary_stage" : "summary_stage1"}
                records={broadVisitFinding1Options as []}
                placeHolder="Select Visit Findings 1"
                onSelect={handleSummaryStage1Change}
              />
              {showOtherReasonField && (
                <InputElement
                  label="Other Reason"
                  name="summary_stage1"
                  rules={[{ required: true, message: "Please Enter Customer Name" }]}
                />
              )}
              <InputElement label="Charger Capacity" name="charger_capacity" />
              <InputElement label="Charger Make Type" name="charger_make_type" />
              {isSuperUser && <InputElement label="Cable Length" name="cable_length_new" />}
              <InputElement
                label="Charger Type"
                name="obc"
                disabled={!(isSuperUser || isMisUser)}
              />
              <InputElement label="Model Name" name="complaint_brand_model_name" />
              <InputElement label="VIN Number" name="vin" />
            </Col>
            <Col span={12}>
              <InputElement label="Sanctioned Load" name="sanctioned_load" />
              <InputElement label="Average Load Utilised" name="avg_load_utilized" />
              <InputElement label="VDC" name="vdc" />
              <InputElement label="MDI" name="mdi" />
              <InputElement label="Cable Gauge" name="cable_gauge" />
              <InputElement label="Remarks" name="remarks" />
              <CheckBoxInput label="Extra Cable Customer Pay" name="extra_cable_customer_pay" />
              <CheckBoxInput label="Billed to Client" name="customer_billing" />
            </Col>
          </Row>

          <Form.Item wrapperCol={{ offset: 19, span: 24 }}>
            <Row>
              <Col>
                <Space size={"middle"}>
                  <Button
                    type="default"
                    onClick={() => {
                      form.resetFields();
                      onGoBack();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={
                      loading ||
                      (isEditMode && !isAdminUser && selectedVisitData?.status === "completed")
                    }
                  >
                    {isEditMode ? "Update" : "Add"}
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </div>
    </>
  );
};

export default VisitForm;
